import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, DownloadService, HttpService, TimeService } from '@dm/core';
import { ToolService } from 'app/common/services';
import { type ChsCollector, type OtaUpgVersionInfo } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { type CheckDetail, type UpgradeModeE, Progress, UPGRADE_QUERY_TYPE } from 'app/feature/share/header-settings/upgrade/upgrade.const';
import { environment } from 'environments/environment';
import { cloneDeep } from 'lodash';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type AutoUpgradeSwitch, type AutoUpgradeSwitchGet, type OtaPkgDownloadProgress, DownloadStartTypeE } from './upgrade';
@Injectable({ providedIn: 'root' })
export class UpgradeService {
    private preCheckStatus = 'null';

    /**
     * 缓存的评估详情数据
     */
    private _otaEvaluateDetailDatas: CheckDetail[];

    /**
     * 缓存存入和读取时，时区可能发生变化，读取的时候时间以UTC时间戳为准
     */
    get otaEvaluateDetailDatas(): CheckDetail[] {
        if (!this._otaEvaluateDetailDatas) {
            return null;
        }
        // 克隆一下避免影响原对象
        const datas = cloneDeep(this._otaEvaluateDetailDatas);
        datas.forEach((data) => {
            // 以UTC时间戳为准更新时间
            data.checkTime = this.timeService.formatUTC(data.utcTimeStamp);
        });
        return datas;
    }

    /**
     * 缓存存入和读取时，时区可能发生变化，存入的时候加上UTC时间戳
     */
    set otaEvaluateDetailDatas(datas) {
        if (!datas) {
            this._otaEvaluateDetailDatas = null;
            return;
        }
        this._otaEvaluateDetailDatas = cloneDeep(datas);
        this._otaEvaluateDetailDatas.forEach((data) => {
            // 存入UTC时间戳
            data.utcTimeStamp = this.timeService.convertToStamps(data.checkTime) * 1000;
        });
    }

    /**
     * 处于升级流程中(热补丁、快速升级、OTA升级)
     */
    inUpgradeProcess = false;

    /**
     * 具体的升级流程
     */
    upgradeProcess: Progress;

    /**
     * 升级方式
     */
    upgradeMode: UpgradeModeE;

    constructor(
        private httpService: HttpService,
        private downloadService: DownloadService,
        private toolService: ToolService,
        private timeService: TimeService,
    ) {}

    /**
     * 获取eService状态
     */
    getEServiceStatus(opt?: BaseRequestOptionsArgs): Observable<ChsCollector> {
        const rest = this.httpService.makeUrl('eService.getChsCollector');
        return this.httpService.get(rest, opt)
            .pipe(
                map(({ data }) => data),
            );
    }

    getOtaUpgVersionInfo(isAuto = true, opt?: BaseRequestOptionsArgs): Observable<OtaUpgVersionInfo[]> {
        const rest = this.httpService.makeUrl('upgrade.getOtaVersionInfo')
            .setParams({ is_auto: isAuto });
        return this.httpService.get(rest, opt)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     *  获取系统信息
     */
    getSystemInfo(options?: BaseRequestOptionsArgs): Observable<any> {
        const rest = this.httpService.makeUrl('system.get');
        return this.httpService.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    getPrechekStatus(): string {
        return this.preCheckStatus;
    }

    setPrecheckStatus(val: string): void {
        this.preCheckStatus = val;
    }

    /**
     * 判断是否所有检查项已完成
     * items 检查项数组
     * doneKey  检查项对象状态字段key值
     * doneStatus 所有能表示完成状态的取值
     */
    ifAllDone(items: any[], doneKey: string, doneStatus: number[]): boolean {
        return !items.some((item) => !doneStatus.includes(Number(item[doneKey])));
    }

    /**
     * 是否展示快速升级特性，高端环境且为release包不展示快速升级特性
     * 返回undefined表明还未获取到设备信息，无法判断
     */
    ifShowUpgrade = (): boolean => {
        const isProdHigh = FeatureConfig.IsNotSupportFastUpgrade;
        if (isProdHigh === undefined) {
            return undefined;
        }
        return !isProdHigh || (environment.env !== 'release' && environment.env !== 'release_crack');
    };

    /**
     * 查询获取导出配置数据路径
     */
    getDataBaseInportPath(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.exportPath');
        return this.httpService.get(rest, options);
    }

    /**
     * 下载配置数据文件
     */
    backupActivatedDataBase(path: string, time: string): Observable<boolean> {
        const rest = this.httpService.makeUrl('upgrade.fileConfig')
            .setParams({ path });
        return this.downloadService.download(rest, 'DataBaseFile' + time + '.dat');
    }

    /**
     * 获取包分发进度详情
     */
    getDistributeDetail(): Observable<any> {
        const rest = this.httpService.makeUrl('upgrade.distributeDetail').setParams({ CMO_QUERY_TYPE: UPGRADE_QUERY_TYPE.Upload });
        return this.httpService.get(rest)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询ota下载包进度
     */
    getOtaPkgDownloadProgress(params: OtaUpgVersionInfo, options?: BaseRequestOptionsArgs): Observable<OtaPkgDownloadProgress> {
        const rest = this.httpService.makeUrl('upgrade.getOtaPkgDownloadProgress').setParams(params);
        return this.httpService.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 下载ota升级包
     */
    downloadOtaPkg(params: OtaUpgVersionInfo, options?: BaseRequestOptionsArgs): Observable<any> {
        let url: string;
        switch (params.startType) {
            case DownloadStartTypeE.Start:
                url = 'upgrade.downloadOtaPkg';
                break;
            case DownloadStartTypeE.Sequel:
                url = 'upgrade.downloadOtaPkg';
                break;
            case DownloadStartTypeE.Pause:
                url = 'upgrade.pauseOtaPkgDownload';
                break;
            case DownloadStartTypeE.Cancel:
                url = 'upgrade.cancelOtaPkgDownload';
                break;
            case DownloadStartTypeE.Delete:
                url = 'upgrade.deleteOtaPkg';
                break;
            default: url = 'upgrade.downloadOtaPkg';
        }
        const rest = this.httpService.makeUrl(url).setParams(params);
        return this.httpService.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 清除评估详情缓存数据
     */
    clearOtaEvaluateDetailDatas(): void {
        this.otaEvaluateDetailDatas = null;
    }

    /**
     * 获取闲时下载配置
     * @param options
     */
    getDownloadInIdleTime(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.getOffPeakDownload');
        return this.httpService.get(rest, options);
    }

    /**
     * 配置闲时下载
     * @param params
     * @param options
     */
    setDownloadInIdleTime(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.setOffPeakDownload');
        return this.httpService.put(rest, params, options);
    }

    /**
     * 获取自动升级开关状态
     * @param options
     */
    getAutoUpgradeSwitch(options?: BaseRequestOptionsArgs): Observable<AutoUpgradeSwitchGet> {
        const rest = this.httpService.makeUrl('upgrade.getAutoUpgradeSwitch');
        return this.httpService.get(rest, options)
        .pipe(
            map(({ data }: { data: AutoUpgradeSwitchGet }) => data),
        );
    }

    /**
     * 设置自动升级开关状态
     * @param options
     */
    setAutoUpgradeSwitch(params: AutoUpgradeSwitch, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('upgrade.setAutoUpgradeSwitch');
        return this.httpService.put(rest, params, options);
    }

    /**
     * 处于分发流程中
     * @param progress 当前步骤
     * @returns boolean
     */
    inDistributeProgress(progress: Progress): boolean {
        return [Progress.packageDistributing, Progress.packageDistributeFail, Progress.packageDistributeSuc].includes(progress);
    }

    /**
     * 处于预检查流程中
     * @param progress 当前步骤
     * @returns boolean
     */
    inPrecheckProgress(progress: Progress): boolean {
        return [Progress.prechecking, Progress.precheckSuc, Progress.precheckFailed].includes(progress);
    }

    /**
     * 处于升级流程中
     * @param progress 当前步骤
     * @returns boolean
     */
    inUpgradeProgress(progress: Progress): boolean {
        return [Progress.executeUpgradeFailed, Progress.upgrading, Progress.upgradeSuc, Progress.upgradeFailed,
            Progress.upgradePause].includes(progress);
    }

    /**
     * 处于验证流程中
     * @param progress 当前步骤
     * @returns boolean
     */
    inVerifyProgress(progress: Progress): boolean {
        return [Progress.verifying, Progress.verifySuc, Progress.verifyFailed].includes(progress);
    }
}
